import React, { useEffect, useRef, useState } from "react";
import {
  SectionWrapper,
  SectionTitle,
  SectionTitleText,
  SectionFooterContainer,
  SectionFooterText,
  SectionFooterLogo,
  SectionFooterTextContainer,
} from "../styles/components/landing";
import { Section0, Section1, Section2 } from "../data/landingHeadings";
import animations from "../animations/animations";
import { BREAKPOINTS } from "../styles/variables/breakpoints";
import { getDeviceInfo } from "../data/utils";

const LandingSection = ({
  content,
  index,
  hoveredIndex,
  setHoveredIndex,
  planesAnimating,
  setActiveAnimation,
}) => {
  const svgRef = useRef(null);
  const sectionTestRef = useRef(null);
  const rippleTextRef = useRef([]);
  const pathsRef = useRef([]);
  const footerWrapperRef = useRef(null);
  const mmRef = useRef(null);

  const [activePage, setActivePage] = useState(false);
  const [animatingReturn, setAnimatingReturn] = useState(false);

  const sectionIndex = content.sectionIndex;
  const fontColor = content.color;
  const heading = content.heading;

  const { isHSWeb, isHSApp } = getDeviceInfo();

  const setRef = el => {
    if (el && !rippleTextRef.current.includes(el)) {
      rippleTextRef.current.push(el);
    }
  };

  const handleClick = () => {
    if (animatingReturn || activePage) return;

    setAnimatingReturn(true);
    setHoveredIndex(index);
    setActiveAnimation(false);
    setActivePage(true);

    const timeline = animations.timeline({
      onComplete: () => {
        window.location.href = content.link;
        setAnimatingReturn(false);
      },
    });

    if (!mmRef.current) return;

    mmRef.current.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      timeline.to(svgRef.current, {
        delay: 0.5,
        yPercent: 100,
        opacity: 0,
        duration: 2,
      });
    });
    mmRef.current.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      timeline.to(footerWrapperRef.current, {
        delay: 0.5,
        yPercent: 100,
        opacity: 0,
        duration: 2,
      });
    });
  };

  useEffect(() => {
    if (!isHSApp || typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const timeline = animations.timeline({
          onComplete: () => {
            setActivePage(false);
            setHoveredIndex(null);
            setActiveAnimation(true);
          },
        });

        if (!mmRef.current) return;

        mmRef.current.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
          timeline.to(svgRef.current, {
            delay: 0.15,
            yPercent: 0,
            opacity: 1,
            duration: 2,
          });
        });

        mmRef.current.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
          timeline.to(footerWrapperRef.current, {
            delay: 0.15,
            yPercent: 0,
            opacity: 1,
            duration: 2,
          });
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      mmRef.current?.revert();
    };
  }, [isHSApp]);

  const renderSectionHeading = () => {
    switch (content.sectionIndex) {
      case 0:
        return <Section0 setRef={setRef} />;
      case 1:
        return <Section1 setRef={setRef} />;
      case 2:
        return <Section2 setRef={setRef} />;
      default:
        return null;
    }
  };

  const endAnimation = timeline => {
    pathsRef.current.forEach(path => {
      animations.killTweensOf(path);

      timeline
        .to(
          path,
          {
            duration: 0.15,
            delay: 0,
            fill: content.color,
            stroke: "none",
          },
          0
        )
        .to(
          path,
          {
            delay: 0.25,
            scaleY: 1,
            duration: 0.5,
            transformOrigin: "center bottom",
            ease: "power3.out",
          },
          0
        );
    }, 0);
    setHoveredIndex(null);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    mmRef.current = animations.matchMedia();

    return () => {
      mmRef.current?.revert();
    };
  }, []);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement) return;

    pathsRef.current = rippleTextRef.current.flatMap(g =>
      Array.from(g.querySelectorAll("path"))
    );

    const handleMouseEnter = () => {
      if (hoveredIndex === index || planesAnimating.current) return;

      setHoveredIndex(index);
    };

    const handleMove = e => {
      if (planesAnimating.current) return;

      if (hoveredIndex !== index) {
        setHoveredIndex(index);
      }

      const mouseX = e.clientX;
      let timeline = animations.timeline({});

      pathsRef.current.forEach(path => {
        const box = path.getBoundingClientRect();
        const distance = Math.abs(box.left + box.width / 2 - mouseX);
        const scale = Math.max(1.5 - distance / 300, 1);

        mmRef.current.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
          timeline
            .to(
              path,
              {
                duration: 0.15,
                delay: 0,
                fill: "#FFFFFF",
                stroke: "#000000",
              },
              0
            )
            .to(
              path,
              {
                scaleY: scale,
                duration: 0.3,
                transformOrigin: "center bottom",
                ease: "power3.out",
              },
              0
            );
        });
      });
    };

    const handleMouseLeave = () => {
      endAnimation(animations.timeline({}));
    };

    svgElement.addEventListener("mouseenter", handleMouseEnter);
    svgElement.addEventListener("mousemove", handleMove);
    svgElement.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      if (!svgElement) return;

      svgElement.removeEventListener("mouseenter", handleMouseEnter);
      svgElement.removeEventListener("mousemove", handleMove);
      svgElement.removeEventListener("mouseleave", handleMouseLeave);

      pathsRef.current.forEach(path => {
        animations.killTweensOf(path);
      });
    };
  }, []);

  useEffect(() => {
    if (!planesAnimating.current || animatingReturn) return;
    endAnimation(animations.timeline({}));
  }, [hoveredIndex]);

  return (
    <SectionWrapper
      ref={sectionTestRef}
      onClick={handleClick}
      $isHSWeb={isHSWeb}
    >
      <SectionTitle ref={svgRef} $desktop>
        {renderSectionHeading()}
      </SectionTitle>
      <SectionTitle ref={footerWrapperRef} $mobile>
        <SectionTitleText $articleId={sectionIndex} $fontColor={fontColor}>
          {heading}
        </SectionTitleText>
        <SectionFooterContainer>
          <SectionFooterLogo src="https://www.datocms-assets.com/40484/1745582565-trident-logo-black.png" />
          <SectionFooterTextContainer>
            <SectionFooterText>Vibe Check</SectionFooterText>
            <SectionFooterText>Refresh your rhythm</SectionFooterText>
          </SectionFooterTextContainer>
        </SectionFooterContainer>
      </SectionTitle>
    </SectionWrapper>
  );
};

export default LandingSection;
